import { type Currency, uuidv7 } from '@btsis/core';

/**
 * What a fulfilment worker did while there was no signal. Field work happens in
 * places without reliable data, so an action is recorded on the device the
 * moment the person takes it and sent when the connection returns —
 * docs/plans/06-portal-and-field.md §6.5.
 *
 * Each action carries the `clientRequestId` minted **when the person acted**,
 * never when it is sent: that is what makes a resend change nothing on the
 * server, whatever happened to the reply the first time
 * (migration `0068`, `client_actions`).
 *
 * Evidence is deliberately not queued. A photo or a video is bytes this queue
 * has nowhere to keep, and the upload path is three round trips the server
 * decides the shape of; capturing evidence therefore asks for a connection and
 * says so. The seam is named rather than half-built.
 */
export type QueuedAction =
  | {
      kind: 'fulfill';
      clientRequestId: string;
      donationId: string;
      referenceCode: string;
      queuedAt: string;
      failure: string | null;
      input: {
        fulfilledInstitutionId?: string;
        cost?: { amountMinor: string; currency: Currency };
      };
    }
  | {
      kind: 'queue';
      clientRequestId: string;
      donationId: string;
      referenceCode: string;
      queuedAt: string;
      failure: string | null;
      input: { fulfillmentInstitutionId?: string };
    };

export type QueuedActionDraft =
  | Omit<Extract<QueuedAction, { kind: 'fulfill' }>, 'clientRequestId' | 'queuedAt' | 'failure'>
  | Omit<Extract<QueuedAction, { kind: 'queue' }>, 'clientRequestId' | 'queuedAt' | 'failure'>;

const STORAGE_KEY = 'btsis.field.actions.v1';

/**
 * Browser storage is per viewer and can be absent, full or blocked, so every
 * read and write is guarded and the app renders correctly without it. What it
 * holds is a person's unsent work, which is why it is read back rather than
 * kept only in memory: closing the app on a bus must not lose it.
 */
/**
 * `localStorage` through `globalThis`, so the queue can be exercised without a
 * DOM. It is per viewer and can be absent, full or blocked — in a private
 * window, with site data cleared, or during a thumbnail capture — so every read
 * and write is guarded and the app renders correctly without it.
 */
function store(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readRaw(): QueuedAction[] {
  try {
    const stored = store()?.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as QueuedAction[]) : [];
  } catch {
    return [];
  }
}

function writeRaw(actions: readonly QueuedAction[]): void {
  try {
    store()?.setItem(STORAGE_KEY, JSON.stringify(actions));
  } catch {
    // A device with no room for the queue still has to take the action; the
    // send below is what makes it durable.
  }
}

export function readQueue(): QueuedAction[] {
  return readRaw();
}

export function enqueueAction(draft: QueuedActionDraft): QueuedAction {
  const action = {
    ...draft,
    clientRequestId: uuidv7(),
    queuedAt: new Date().toISOString(),
    failure: null,
  } as QueuedAction;
  writeRaw([...readRaw(), action]);
  return action;
}

export function removeAction(clientRequestId: string): QueuedAction[] {
  const remaining = readRaw().filter((action) => action.clientRequestId !== clientRequestId);
  writeRaw(remaining);
  return remaining;
}

export function setActionFailure(clientRequestId: string, failure: string | null): QueuedAction[] {
  const marked = readRaw().map((action) =>
    action.clientRequestId === clientRequestId ? { ...action, failure } : action,
  );
  writeRaw(marked);
  return marked;
}

/** The donations this queue is already carrying an action for, so a screen can say so. */
export function pendingDonationIds(actions: readonly QueuedAction[]): Set<string> {
  return new Set(actions.map((action) => action.donationId));
}
